import { FunctionComponent, useMemo, useState } from 'react'
import { Button, ButtonToolbar, Form, Modal } from 'react-bootstrap'
import { useWorkroom } from './WorkroomContext'
import { Controller, useForm } from 'react-hook-form'
import { IWorkroomFolder, WorkroomItemType, IWorkroomDocument } from 'Frontend/scripts/models/workrooms.model'
import { updateFile, updateFolder, getFolders, getAncestors } from 'Frontend/scripts/services/workrooms.service'
import { useTranslation } from 'react-i18next'

interface MoveItemForm {
  destination: string
}

const WorkroomMoveItemButton: FunctionComponent = () => {
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const { selectedFiles, currentWorkroomId, currentFolder, isLoading, fetchFilesAndFolders } = useWorkroom()
  const [availableDestinations, setAvailableDestinations] = useState<IWorkroomFolder[]>([])
  const { t } = useTranslation()

  const GetAvailableDestinations = async () => {
    let parentFolder: IWorkroomFolder[] = []

    const foldersResponse = await getFolders(currentWorkroomId as string, currentFolder)

    if (currentFolder !== 'root') {
      const parentResponse = await getAncestors(currentWorkroomId as string, currentFolder)
      parentFolder = parentResponse.length > 1 ? [parentResponse[parentResponse.length - 2]] : []
    }

    return [...foldersResponse, ...parentFolder]
  }

  useMemo(async () => {
    if (!isOpen || selectedFiles.length <= 0) return

    setAvailableDestinations(await GetAvailableDestinations())
  }, [isOpen])

  const {
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<MoveItemForm>()

  const canMoveFileOrFolder = useMemo(() => {
    return selectedFiles.length > 0
  }, [selectedFiles])

  const onSubmit = (data: MoveItemForm) => {
    const updateFiledsAndFoldersPromises = selectedFiles.map((item) => {
      if (item.type === WorkroomItemType.FILE) {
        const editedItem = { ...item, workroomFolderId: data.destination !== 'null' ? data.destination : null }
        return updateFile(currentWorkroomId as string, editedItem as unknown as IWorkroomDocument)
      } else {
        const editedItem = { ...item, parentWorkroomFolderId: data.destination !== 'null' ? data.destination : null }
        return updateFolder(currentWorkroomId as string, editedItem as unknown as IWorkroomFolder)
      }
    })

    return Promise.all(updateFiledsAndFoldersPromises).then(() => {
      fetchFilesAndFolders()
      handleClose()
    })
  }

  const handleClose = () => {
    reset()
    setIsOpen(false)
  }

  return (
    <>
      <Button variant="secondary" className="fw-normal" disabled={!canMoveFileOrFolder} onClick={() => setIsOpen(true)}>
        <i className={`fa fa-arrows-alt small me-1`} />
        {t('workrooms.documents.move-folder-or-file')}
      </Button>
      <Modal show={isOpen} centered>
        <Modal.Header className="h4 mb-0"> {t('workrooms.documents.move-folders-or-files')}</Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit(onSubmit)}>
            <Form.Group>
              <Form.Label htmlFor="folderPicker" className="mb-1">
                {t('workrooms.documents.move-folder-choose-folder')}
              </Form.Label>
              <Controller
                name="destination"
                control={control}
                rules={{ required: t('workrooms.documents.move-folder-or-file-validation-text') }}
                render={({ field }) => (
                  <Form.Select
                    id="folderPicker"
                    {...field}
                    disabled={isLoading}
                    as="select"
                    className={`form-control ${errors?.destination ? 'is-invalid' : ''}`}
                  >
                    <option> {t('workrooms.documents.move-folder-choose-folder')}</option>
                    {currentFolder !== 'root' && <option value="null">{t('workrooms.documents.document')}</option>}
                    {availableDestinations.map((folder) => (
                      <option key={`destination-${folder.id}`} value={folder.id}>
                        {folder.name}
                      </option>
                    ))}
                  </Form.Select>
                )}
              />
              {errors?.destination && <p className="invalid-feedback">{errors?.destination.message}</p>}
            </Form.Group>
            <ButtonToolbar className="justify-content-between mt-3">
              <Button type="submit" variant="secondary" disabled={isLoading}>
                {t('save')}
              </Button>
              <Button type="button" variant="outline-secondary" onClick={() => handleClose()} disabled={isLoading}>
                {t('abort')}
              </Button>
            </ButtonToolbar>
          </Form>
        </Modal.Body>
      </Modal>
    </>
  )
}
export default WorkroomMoveItemButton
